import { type ReactNode } from "react";

/**
 * The scaffold every placeholder screen uses in the shell (tasks 11.4–11.15 replace the bodies with
 * real views). It encodes the four states a screen must handle honestly (design 12.3):
 *
 * - loading     — data is on its way.
 * - empty       — there is genuinely nothing to show, WITH a cause ("no source enrolled", "source
 *                 silent", "filter too narrow") rather than a blank panel.
 * - error       — the call failed; the message explains what and offers no false reassurance.
 * - restricted  — the operator's role may not see this; shown disabled with a reason, server
 *                 enforces regardless.
 *
 * Keeping the scaffold here means the shell can route to every screen now, each already a11y-shaped
 * (a labelled region, a heading), and later tasks only fill the `ready` body.
 */

export type ScreenState =
  | { readonly kind: "loading" }
  | { readonly kind: "empty"; readonly cause: string }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "restricted"; readonly reason: string }
  | { readonly kind: "ready" };

export function ScreenScaffold(props: {
  title: string;
  subtitle?: string;
  /** The current state; a placeholder screen defaults to a descriptive "empty". */
  state: ScreenState;
  /** The real content, rendered only when `state.kind === "ready"`. */
  children?: ReactNode;
}): ReactNode {
  return (
    <section aria-label={props.title}>
      <header className="screen__header">
        <h1 className="screen__title">{props.title}</h1>
        {props.subtitle !== undefined && <p className="screen__subtitle">{props.subtitle}</p>}
      </header>
      <ScreenBody state={props.state}>{props.children}</ScreenBody>
    </section>
  );
}

function ScreenBody(props: { state: ScreenState; children?: ReactNode }): ReactNode {
  const { state } = props;
  switch (state.kind) {
    case "loading":
      return (
        <div className="state-box" data-kind="loading" role="status" aria-live="polite">
          <strong>Loading…</strong>
          <span>Fetching data for this view.</span>
        </div>
      );
    case "empty":
      return (
        <div className="state-box" data-kind="empty">
          <strong>Nothing to show</strong>
          <span>{state.cause}</span>
        </div>
      );
    case "error":
      return (
        <div className="state-box" data-kind="error" role="alert">
          <strong>This view could not load</strong>
          <span>{state.message}</span>
        </div>
      );
    case "restricted":
      return (
        <div className="state-box" data-kind="restricted">
          <strong>Not available for your role</strong>
          <span>{state.reason}</span>
        </div>
      );
    case "ready":
      return <>{props.children}</>;
  }
}
